import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { getDashboardPath } from '../utils/authRoutes';
import { Mail, Lock, User, Phone, AlertCircle, Briefcase, Eye, EyeOff } from 'lucide-react';
import { AUTH } from '../constants/testIds';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { hasPasswordSpaces, isValidEmail, normalizeEmail } from '../utils/authForms';
import AuthLayout from '../components/auth/AuthLayout';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const isProviderRegistration = searchParams.get('role') === 'provider';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: isProviderRegistration ? 'provider' : 'client',
    provider_type: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [resendWait, setResendWait] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const roleType = isProviderRegistration ? (formData.provider_type || 'provider') : 'client';
  const fullName = `${formData.first_name.trim()} ${formData.last_name.trim()}`.trim();

  const validateForm = () => {
    const cleanEmail = normalizeEmail(formData.email);
    if (!formData.first_name.trim()) {
      setError('Ingresa tus nombres.');
      return false;
    }
    if (!formData.last_name.trim()) {
      setError('Ingresa tus apellidos.');
      return false;
    }
    if (!isValidEmail(cleanEmail)) {
      setError('Ingresa un correo válido.');
      return false;
    }
    if (hasPasswordSpaces(formData.password) || hasPasswordSpaces(formData.confirmPassword)) {
      setError('La contraseña no debe contener espacios.');
      return false;
    }
    if (formData.password.length < 8 || !/[A-Za-z]/.test(formData.password) || !/\d/.test(formData.password)) {
      setError('La contraseña debe tener al menos 8 caracteres e incluir letras y números.');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return false;
    }
    if (formData.role === 'provider' && !formData.provider_type) {
      setError('Selecciona el tipo de proveedor.');
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (resendWait <= 0) return undefined;
    const timer = setTimeout(() => setResendWait((current) => Math.max(current - 1, 0)), 1000);
    return () => clearTimeout(timer);
  }, [resendWait]);

  const getErrorMessage = (err) => {
    if (!err.response || err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
      return 'No se pudo conectar con el servidor. Verifica que el backend esté activo.';
    }
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join(' ');
    return 'Algo salió mal. Por favor intenta de nuevo.';
  };

  const sendOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-email-otp`, {
        email: normalizeEmail(formData.email)
      });
      setOtpSent(true);
      setResendWait(60);
      setError('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    if (!isProviderRegistration && !otpSent) {
      await sendOtp();
      return;
    }

    if (!isProviderRegistration && !otpCode.trim()) {
      setError('Ingresa el código de verificación.');
      return;
    }

    setLoading(true);
    const payload = {
      email: normalizeEmail(formData.email),
      password: formData.password,
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      name: fullName,
      phone: formData.phone,
      role: formData.role,
      provider_type: formData.role === 'provider' ? formData.provider_type : undefined,
      otp_code: formData.role === 'client' ? otpCode.trim() : undefined
    };

    const result = await register(payload);
    setLoading(false);

    if (result.success) {
      navigate(getDashboardPath(result.data), { replace: true });
    } else {
      setError(result.error);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <AuthLayout mode="register" roleType={roleType} stepLabel={isProviderRegistration ? 'Paso 1 de 2' : otpSent ? 'Paso 2 de 2' : 'Paso 1 de 2'}>
      <h2>{isProviderRegistration ? 'Crea tu cuenta de proveedor' : 'Crea tu cuenta en PROVI'}</h2>
      <p className="subtitle">
        {isProviderRegistration
          ? 'Completa tus datos y elige el tipo de servicio que vas a ofrecer.'
          : 'Completa tus datos y continúa con la verificación por correo.'}
      </p>

      {error && (
        <div className="provi-auth-error">
          <AlertCircle size={18} />
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="provi-auth-form two-col">
        <label className="provi-auth-field" htmlFor="first_name">
          <span>Nombres</span>
          <div className="provi-auth-input-wrap">
            <User size={19} />
            <input
              id="first_name"
              type="text"
              data-testid={AUTH.registerNameInput}
              value={formData.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              required
              placeholder="Juan Carlos"
            />
          </div>
        </label>

        <label className="provi-auth-field" htmlFor="last_name">
          <span>Apellidos</span>
          <div className="provi-auth-input-wrap">
            <User size={19} />
            <input
              id="last_name"
              type="text"
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              required
              placeholder="Pérez López"
            />
          </div>
        </label>

        <label className="provi-auth-field full" htmlFor="email">
          <span>Correo electrónico</span>
          <div className="provi-auth-input-wrap">
            <Mail size={19} />
            <input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              data-testid={AUTH.registerEmailInput}
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              placeholder="tu@email.com"
            />
          </div>
        </label>

        <label className="provi-auth-field full" htmlFor="phone">
          <span>Teléfono <small style={{ color: '#9ca3af' }}>(opcional)</small></span>
          <div className="provi-auth-input-wrap">
            <Phone size={19} />
            <input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+593 999 999 999"
            />
          </div>
        </label>

        <PasswordField
          id="password"
          label="Contraseña"
          value={formData.password}
          onChange={(value) => handleChange('password', value)}
          visible={showPassword}
          setVisible={setShowPassword}
          testId={AUTH.registerPasswordInput}
          name="password"
          autoComplete="new-password"
        />

        <PasswordField
          id="confirmPassword"
          label="Confirmar contraseña"
          value={formData.confirmPassword}
          onChange={(value) => handleChange('confirmPassword', value)}
          visible={showConfirmPassword}
          setVisible={setShowConfirmPassword}
          name="confirmPassword"
          autoComplete="new-password"
        />

        {isProviderRegistration && (
          <label className="provi-auth-field full" htmlFor="provider_type">
            <span>Tipo de proveedor</span>
            <div className="provi-auth-input-wrap">
              <Briefcase size={19} />
              <Select value={formData.provider_type} onValueChange={(value) => handleChange('provider_type', value)}>
                <SelectTrigger className="provi-auth-select bg-transparent border-0 text-white p-0 h-auto focus:ring-0">
                  <SelectValue placeholder="Selecciona tipo de proveedor" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1b1f] border-white/10 text-white">
                  <SelectItem value="taxi">Taxista</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="business">Empresa</SelectItem>
                  <SelectItem value="local">Local / Restaurante</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </label>
        )}

        {!isProviderRegistration && otpSent && (
          <div className="provi-auth-field full">
            <label htmlFor="otpCode">Código de verificación</label>
            <div className="provi-auth-input-wrap">
              <Mail size={19} />
              <input
                id="otpCode"
                type="text"
                inputMode="numeric"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                placeholder="123456"
              />
            </div>
            <button
              type="button"
              disabled={loading || resendWait > 0}
              onClick={sendOtp}
              className="provi-auth-minor-link"
              style={{ marginTop: 10, background: 'transparent', border: 0, cursor: 'pointer' }}
            >
              {resendWait > 0 ? `Reenviar código en ${resendWait}s` : 'Reenviar código'}
            </button>
          </div>
        )}

        <div className="provi-auth-field full">
          <button
            type="submit"
            data-testid={AUTH.registerSubmitButton}
            disabled={loading}
            className="provi-auth-primary"
          >
            {loading
              ? 'Procesando...'
              : !isProviderRegistration && !otpSent
                ? 'Continuar y verificar correo'
                : !isProviderRegistration
                  ? 'Verificar y crear cuenta'
                  : 'Crear cuenta'}
          </button>
        </div>
      </form>

      <div className="provi-auth-safe">
        <span>🔒</span>
        <span>Tus datos estarán protegidos. Usaremos tu correo solo para verificar tu cuenta.</span>
      </div>

      <div className="provi-auth-links" style={{ marginTop: 16 }}>
        ¿Ya tienes una cuenta? <Link to={isProviderRegistration ? '/login?role=provider' : '/login'}>Inicia sesión</Link>
      </div>
    </AuthLayout>
  );
};

const PasswordField = ({ id, label, value, onChange, visible, setVisible, testId, name, autoComplete }) => (
  <label className="provi-auth-field" htmlFor={id}>
    <span>{label}</span>
    <div className="provi-auth-input-wrap">
      <Lock size={19} />
      <input
        id={id}
        name={name}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        minLength={8}
        placeholder="********"
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        className="provi-auth-eye"
        aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      >
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  </label>
);

export default RegisterPage;
